import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../../basic/index';
import { Spinner } from '../../../../../common/index';
import ErasmusChallengesForm from './ErasmusChallengesForm/ErasmusChallengesForm';
import {
  useGetErasmusChallengesQuery,
  useGetErasmusChallengeQuery,
  useSaveErasmusChallengeMutation,
} from '../../../../../../redux/services/erasmusChallenges';
import { routes } from '../../../../../../utils/helpers/ApiClient';
import { useGetErasmusConsortiumsQuery } from '../../../../../../redux/services/erasmusConsortium';
import { useGetErasmusUniversitiesQuery } from '../../../../../../redux/services/erasmusUniversities';
import { useGetStudyProgramsQuery } from '../../../../../../redux/services/study_programs';
import { useGetErasmusMobilityTypesQuery } from '../../../../../../redux/services/erasmusMobilityTypes';
import { useGetErasmusMobilitySubtypesQuery } from '../../../../../../redux/services/erasmusMobilitySubtypes';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';
import dayjs from 'dayjs';

const ErasmusChallengesEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const isPreview = mode === 'preview';

  const { data: challengeDetail, isLoading } = useGetErasmusChallengeQuery(id, {
    skip: !isEdit && !isPreview,
  });
  const { saveSuccess: toastSaveSuccess } = useToastMessages();
  const [saveErasmusChallenge, { isSuccess: isSaveSuccess }] = useSaveErasmusChallengeMutation();
  const { refetch } = useGetErasmusChallengesQuery();
  const { data: consortiums } = useGetErasmusConsortiumsQuery();
  const { data: universities } = useGetErasmusUniversitiesQuery();
  const { data: studyPrograms } = useGetStudyProgramsQuery();
  const { data: erasmusMobilityTypes } = useGetErasmusMobilityTypesQuery();
  const { data: erasmusMobilitySubtypes } = useGetErasmusMobilitySubtypesQuery();

  useEffect(() => {
    if (isSaveSuccess) {
      toastSaveSuccess();
      navigate(routes.cmsErasmusChallenges);
      refetch();
    }
  }, [isSaveSuccess]);

  const title =
    (isCreate && t('Nová výzva')) || t('Editovanie výzvy');

  if (isLoading || (isEdit && !challengeDetail) || !consortiums || !universities || !studyPrograms || !erasmusMobilityTypes || !erasmusMobilitySubtypes) {
    return <Spinner />;
  }

  // Výpočet aktuálneho akademického roku
  const getCurrentAcademicYear = () => {
    const now = dayjs();
    const currentYear = now.year();
    const currentMonth = now.month(); // 0 = január, 8 = september

    // Ak je september alebo neskorší, akademický rok začína tento rok
    // Inak začína minulý rok
    const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
    return `${startYear}/${startYear + 1}`;
  };
  
  const initials = isEdit || isPreview
    ? {
        ...challengeDetail.challenge,
        deadline: dayjs(challengeDetail.challenge.deadline),
      }
    : {
        name: undefined,
        deadline: undefined,
        consortium_id: undefined,
        university_id: undefined,
        academic_year: getCurrentAcademicYear(), // Predvolený aktuálny akademický rok
        mobility_type_id: undefined,
        mobility_subtype_id: undefined,
        study_year: undefined,
        study_program_id: undefined,
        study_degree: undefined,
      };

  if (!initials) {
    return <Spinner />;
  }

  return (
    <>
      {isPreview ? null : (
        <AppBarCMS
          title={title}
          backUrl={routes.cmsErasmusChallenges}
          backUrlTooltip={t('Výzvy')}
          permissionsBasic={'erasmus-challenges'}
        />
      )}
      <div style={{ margin: '10px' }}>
        <ErasmusChallengesForm
          initials={initials}
          isEdit={isEdit}
          isPreview={isPreview}
          consortiums={consortiums}
          universities={universities}
          studyPrograms={studyPrograms}
          erasmusMobilityTypes={erasmusMobilityTypes}
          erasmusMobilitySubtypes={erasmusMobilitySubtypes}
          onSubmit={(values) => {
            saveErasmusChallenge({ body: values, id: id });
          }}
        />
      </div>
    </>
  );
};

export default ErasmusChallengesEdit;
